// CreateInteriorQuatation.js - data-entry form for a full interior fit-out quotation.
// Sections are fully dynamic: the user can add/rename/reorder/remove any number of them.
// Each section is either "Priced" (full Room/Width/Height/Depth/Rate/Total fields, rolls into the
// totals block) or "Informational" (Section-C-style: Unit Name/Qty/Details/Image only,
// always excluded from every total).
import { AlertTriangle, ArrowLeft, CheckCircle2, Eye, Info, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import RichTextEditor from '../ui/RichTextEditor';
import { formatApiError, interiorQuotationApi, uploadAllQuotationImages } from './interiorQuotationApi';
import {
  addRoomToList,
  computeQuotationTotals,
  createRoom,
  createSection,
  createSectionCRoom,
  generateId,
  moveRoomInList,
  removeRoomFromList,
  renameRoomInList,
  round2,
  sumRoomTotal,
  sumSectionTotal,
  toggleRoomCollapsedInList,
  toNumber,
  updateRoomItemsInList,
} from './interiorQuotationModel';
import { createDefaultTemplatesHtml } from './interiorQuotationTemplates';
import { formatQuotationVersion } from './interiorQuotationStore';
import ConfirmDialog from './ConfirmDialog';
import PreviewInteriorQuatation from './PreviewInteriorQuatation';
import RoomAccordion from './RoomAccordion';
import RoomItemsTable from './RoomItemsTable';
import SectionAccordion from './SectionAccordion';
import SectionCItemsTable from './SectionCItemsTable';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const formatCurrency = (amount = 0) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);

const cardClass = 'rounded-2xl border border-gray-200 bg-white p-4 shadow-sm';
const labelClass = 'text-xs font-semibold text-gray-500';
const inputClass =
  'mt-1 h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30';
const percentInputClass =
  'h-9 w-20 rounded-lg border border-gray-200 bg-gray-50 px-2 text-right text-sm text-gray-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30';

/** Strip a leading zero so typing "5" over default "0" becomes "5", not "05". */
const sanitizePercentInput = (raw) => {
  if (raw === '' || raw === '.' || raw === '0.') return raw;
  if (/^0+\d/.test(raw)) return raw.replace(/^0+(?=\d)/, '');
  return raw;
};
const addRoomBtnClass =
  'inline-flex items-center gap-2 self-start rounded-full border border-dashed border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-teal-400 hover:text-teal-700';
const addSectionBtnClass =
  'inline-flex items-center gap-2 self-start rounded-full border border-dashed border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-teal-400 hover:text-teal-700';

const collectMissingUnitNames = (rooms) => {
  const missing = [];
  rooms.forEach((room) => {
    room.items.forEach((item, idx) => {
      if (!item.unitName || !item.unitName.trim()) {
        missing.push(`${room.name || 'Unnamed room'} - row ${idx + 1}`);
      }
    });
  });
  return missing;
};

/** Clone a loaded snapshot for supersede: new nested ids + today's date (new lineage). */
const cloneSnapshotForNewLineage = (record) => {
  const header = {
    ...(record?.header || {}),
    dateCreated: getTodayDate(),
  };

  const sections = (record?.sections || []).map((section) => ({
    ...section,
    id: generateId('section'),
    rooms: (section.rooms || []).map((room) => ({
      ...room,
      id: generateId('room'),
      items: (room.items || []).map((item) => ({
        ...item,
        id: generateId('item'),
        // Keep file_id image refs; drop local-only dataUrls (re-upload if needed).
        image: item.image?.file_id
          ? { name: item.image.name, file_id: item.image.file_id }
          : item.image?.dataUrl
            ? { name: item.image.name, dataUrl: item.image.dataUrl }
            : item.image || null,
      })),
    })),
  }));

  const paymentSchedule = (record?.paymentSchedule || []).map((row) => ({
    ...row,
    id: generateId('ps'),
  }));

  return {
    header,
    sections,
    designFeePercent: record?.designFeePercent ?? '',
    gstPercent: record?.gstPercent ?? '18',
    paymentSchedule,
    templates: record?.templates || {},
  };
};

const CreateInteriorQuatation = () => {
  useScreenLogging('CreateInteriorQuotation', {
    component: 'CreateInteriorQuotation',
    screenType: 'interior-quotation-create',
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Remount when navigation intent changes (Edit vs Supersede vs New) so state cannot leak.
  const remountKey = [
    location.state?.source || 'new',
    location.state?.quoteId || location.state?.quotationId || '',
    location.state?.supersedeQuoteId || '',
    location.state?.intent || '',
    location.key,
  ].join('|');

  return <CreateInteriorQuatationForm key={remountKey} />;
};

const CreateInteriorQuatationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation state from list: siteId required for API; source api|supersede|new.
  const siteId = location.state?.siteId || null;
  const editingId = location.state?.quotationId || null;
  const navSource = location.state?.source || null;
  const navQuoteId = location.state?.quoteId || editingId || null;
  const supersedeQuoteId = location.state?.supersedeQuoteId || null;
  const preferDraftCreate = location.state?.intent === 'draft';
  const supersedeFromInvoice = location.state?.supersedeFromInvoice || null;
  const isSuperseding = Boolean(supersedeQuoteId) || navSource === 'supersede';

  const [apiQuoteId, setApiQuoteId] = useState(() =>
    isSuperseding ? null : navQuoteId || null
  );
  const [quoteStatus, setQuoteStatus] = useState(() =>
    isSuperseding ? null : location.state?.status || null
  );
  const [metaVersion, setMetaVersion] = useState(null);

  const [isLoadingRecord, setIsLoadingRecord] = useState(
    () => Boolean(supersedeQuoteId || (!isSuperseding && navQuoteId))
  );
  const [isSaving, setIsSaving] = useState(false);

  const [header, setHeader] = useState({
    dateCreated: getTodayDate(),
    documentOwner: '',
    client: '',
    project: '',
    companyName: '',
  });

  const [sections, setSections] = useState([
    createSection({
      name: 'Section A - Woodwork',
      isPriced: true,
      collapsed: false,
      rooms: [createRoom('New Room')],
    }),
    createSection({
      name: 'Section B - On Actuals / Site Work & Similar',
      isPriced: true,
      collapsed: true,
      rooms: [createRoom('New Room')],
    }),
    createSection({
      name: 'Section C - Can Be Paid to Local Vendor',
      isPriced: false,
      collapsed: true,
      rooms: [createSectionCRoom('New Category')],
    }),
  ]);

  const [designFeePercent, setDesignFeePercent] = useState('');
  const [gstPercent, setGstPercent] = useState('18');
  const [paymentSchedule, setPaymentSchedule] = useState([
    { id: generateId('ps'), label: 'Before Design Starts', percent: '' },
    { id: generateId('ps'), label: 'After Design Finalized, Before Production Begins', percent: '' },
    { id: generateId('ps'), label: 'Before Materials Shifted to Site', percent: '' },
  ]);

  const [templates, setTemplates] = useState(() => createDefaultTemplatesHtml(''));
  const [feedback, setFeedback] = useState({ success: '', error: '' });
  const [showValidation, setShowValidation] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [draftToast, setDraftToast] = useState('');

  const baselineFingerprintRef = useRef(null);
  const allowLeaveRef = useRef(false);

  const formFingerprint = useMemo(
    () =>
      JSON.stringify({
        header,
        sections,
        designFeePercent,
        gstPercent,
        paymentSchedule,
        templates,
      }),
    [header, sections, designFeePercent, gstPercent, paymentSchedule, templates]
  );

  // Capture baseline once the form is ready (after load, or immediately for a new quote).
  useEffect(() => {
    if (isLoadingRecord) {
      baselineFingerprintRef.current = null;
      return;
    }
    if (baselineFingerprintRef.current === null) {
      baselineFingerprintRef.current = formFingerprint;
    }
  }, [isLoadingRecord, formFingerprint]);

  const isDirty =
    !isLoadingRecord &&
    baselineFingerprintRef.current !== null &&
    formFingerprint !== baselineFingerprintRef.current;

  // Browser refresh / tab close
  useEffect(() => {
    if (!isDirty || allowLeaveRef.current) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const requestLeave = (to = '/onboarding-interior-quotation') => {
    if (allowLeaveRef.current || !isDirty) {
      allowLeaveRef.current = true;
      navigate(to);
      return;
    }
    setLeaveConfirmOpen(true);
  };

  const confirmLeave = () => {
    allowLeaveRef.current = true;
    setLeaveConfirmOpen(false);
    navigate('/onboarding-interior-quotation');
  };

  const cancelLeave = () => {
    setLeaveConfirmOpen(false);
  };

  const markCleanAndLeaveSoon = () => {
    allowLeaveRef.current = true;
    baselineFingerprintRef.current = formFingerprint;
    window.setTimeout(() => navigate('/onboarding-interior-quotation'), 1100);
  };

  const markCleanStayOnPage = (sectionsSnapshot) => {
    baselineFingerprintRef.current = JSON.stringify({
      header,
      sections: sectionsSnapshot ?? sections,
      designFeePercent,
      gstPercent,
      paymentSchedule,
      templates,
    });
  };

  const applyLoadedRecord = (record, { forSupersede = false } = {}) => {
    if (!record) return;

    const source = forSupersede ? cloneSnapshotForNewLineage(record) : record;

    if (source.header) setHeader(source.header);
    if (source.sections) setSections(source.sections);
    if (source.designFeePercent != null) setDesignFeePercent(source.designFeePercent);
    if (source.gstPercent != null) setGstPercent(source.gstPercent);
    if (source.paymentSchedule) setPaymentSchedule(source.paymentSchedule);
    if (source.templates) setTemplates(source.templates);

    if (forSupersede) {
      // New lineage — never bind to the old quote_id (that would cause PUT instead of supersede).
      setApiQuoteId(null);
      setQuoteStatus(null);
      setMetaVersion(null);
      return;
    }

    setMetaVersion({
      majorVersion: record.majorVersion,
      minorVersion: record.minorVersion,
      version: record.version,
    });
    if (record.status) setQuoteStatus(record.status);
    if (record.quoteId) {
      setApiQuoteId(record.quoteId);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!siteId) {
        setIsLoadingRecord(false);
        setFeedback({
          success: '',
          error: 'No site selected. Go back and choose a site before creating a quotation.',
        });
        return;
      }

      const loadId = supersedeQuoteId || (!isSuperseding ? navQuoteId : null);
      if (!loadId) {
        setIsLoadingRecord(false);
        return;
      }

      setIsLoadingRecord(true);
      try {
        const record = await interiorQuotationApi.getQuotation(siteId, loadId);
        if (cancelled) return;
        applyLoadedRecord(record, { forSupersede: isSuperseding });
        if (isSuperseding) {
          setFeedback({
            success: `Starting a new lineage from ${
              supersedeFromInvoice || 'the previous quotation'
            }. Edit as needed, then Supersede & Send — this creates a new quote_id; the previous one becomes superseded.`,
            error: '',
          });
        }
      } catch (error) {
        if (!cancelled) {
          setFeedback({ success: '', error: formatApiError(error) });
        }
      } finally {
        if (!cancelled) setIsLoadingRecord(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [siteId, supersedeQuoteId, navQuoteId, isSuperseding, supersedeFromInvoice]);

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
    setFeedback({ success: '', error: '' });
  };

  const handlePaymentRowChange = (id, field, value) => {
    setPaymentSchedule((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  // ---- Section-level actions --------------------------------------------------------

  const updateSection = (sectionId, patch) =>
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)));

  const addSection = () =>
    setSections((prev) => [...prev, createSection({ name: `New Section ${prev.length + 1}` })]);

  const removeSection = (sectionId) => setSections((prev) => prev.filter((s) => s.id !== sectionId));

  const moveSection = (sectionId, direction) =>
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === sectionId);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });

  // ---- Room-level actions, bound to a given section ----------------------------------

  const roomActionsFor = (section) => ({
    toggleCollapsed: (roomId) =>
      updateSection(section.id, { rooms: toggleRoomCollapsedInList(section.rooms, roomId) }),
    rename: (roomId, name) => updateSection(section.id, { rooms: renameRoomInList(section.rooms, roomId, name) }),
    updateItems: (roomId, items) =>
      updateSection(section.id, { rooms: updateRoomItemsInList(section.rooms, roomId, items) }),
    addRoom: (factory) => updateSection(section.id, { rooms: addRoomToList(section.rooms, factory()) }),
    removeRoom: (roomId) => updateSection(section.id, { rooms: removeRoomFromList(section.rooms, roomId) }),
    moveRoom: (roomId, direction) =>
      updateSection(section.id, { rooms: moveRoomInList(section.rooms, roomId, direction) }),
  });

  // ---- Totals -------------------------------------------------------------------------

  const {
    pricedSectionTotals,
    totalPriced,
    totalAfterDiscount,
    designFeeAmount,
    amountWithDesignFee,
    gstAmount,
    finalAmount,
    paymentRows,
  } = useMemo(
    () => computeQuotationTotals({ sections, designFeePercent, gstPercent, paymentSchedule }),
    [sections, designFeePercent, gstPercent, paymentSchedule]
  );

  const paymentPercentSum = useMemo(
    () => round2(paymentSchedule.reduce((sum, row) => sum + toNumber(row.percent), 0)),
    [paymentSchedule]
  );
  const paymentSumWarning = paymentPercentSum !== 100;

  const validationErrors = useMemo(() => {
    const errors = [];

    if (!header.client?.trim()) {
      errors.push('Client name is required.');
    }
    if (!header.project?.trim()) {
      errors.push('Project name is required.');
    }

    sections.forEach((section) => {
      const missing = collectMissingUnitNames(section.rooms);
      if (missing.length) {
        errors.push(`${section.name || 'Unnamed section'} - Unit Name required: ${missing.join(', ')}`);
      }
    });

    const pctFields = [
      ['Design & Coordination Fee %', designFeePercent],
      ['GST %', gstPercent],
      ...sections
        .filter((s) => s.isPriced)
        .map((s) => [`"${s.name || 'Unnamed section'}" Discount %`, s.discountPercent]),
      ...paymentSchedule.map((row) => [`Payment schedule "${row.label}" %`, row.percent]),
    ];
    pctFields.forEach(([label, value]) => {
      const n = toNumber(value);
      if (n < 0 || n > 100) errors.push(`${label} must be between 0 and 100.`);
    });

    if (paymentPercentSum !== 100) {
      errors.push(`Payment Schedule must sum to 100% (currently ${paymentPercentSum}%).`);
    }

    return errors;
  }, [header.client, header.project, sections, designFeePercent, gstPercent, paymentSchedule, paymentPercentSum]);

  const buildSnapshot = (status) => ({
    quoteId: isSuperseding ? undefined : apiQuoteId || undefined,
    siteId,
    status,
    header,
    sections,
    designFeePercent,
    gstPercent,
    paymentSchedule,
    templates,
    finalAmount,
  });

  const materializeSectionsForApi = async () => {
    const hasEmbeddedImages = JSON.stringify(sections).includes('"dataUrl"');
    if (!hasEmbeddedImages) return sections;
    const uploaded = await uploadAllQuotationImages(siteId, sections);
    setSections(uploaded);
    return uploaded;
  };

  const applySavedMeta = (saved) => {
    setApiQuoteId(saved.quoteId);
    if (saved.status) setQuoteStatus(saved.status);
    setMetaVersion({
      majorVersion: saved.majorVersion,
      minorVersion: saved.minorVersion,
      version: saved.version,
    });
  };

  const isDraftQuote = quoteStatus === 'draft';
  const canSaveAsDraft = !isSuperseding && (!apiQuoteId || isDraftQuote);
  const isClientFacingEdit =
    Boolean(apiQuoteId) && (quoteStatus === 'uploaded' || quoteStatus === 'reopened');

  const handleSaveDraft = async ({ stayOnPage = false } = {}) => {
    if (isSaving) return;
    if (!siteId) {
      setFeedback({ success: '', error: 'Select a site before saving a draft.' });
      return;
    }
    if (!canSaveAsDraft) {
      setFeedback({
        success: '',
        error: 'Submitted quotations cannot return to draft. Use Save Revision or Supersede.',
      });
      return;
    }

    setShowValidation(true);
    if (!header.client?.trim() || !header.project?.trim()) {
      setFeedback({
        success: '',
        error: 'Client and project are required before saving a draft.',
      });
      return;
    }
    if (paymentPercentSum !== 100) {
      setFeedback({
        success: '',
        error: `Payment Schedule must sum to 100% (currently ${paymentPercentSum}%).`,
      });
      return;
    }

    setIsSaving(true);
    setFeedback({ success: '', error: '' });

    try {
      const sectionsToSend = await materializeSectionsForApi();
      const payload = { ...buildSnapshot('draft'), sections: sectionsToSend };
      let saved;

      if (apiQuoteId && isDraftQuote) {
        saved = await interiorQuotationApi.updateQuotation(siteId, apiQuoteId, payload);
      } else {
        saved = await interiorQuotationApi.createQuotation(siteId, payload, { draft: true });
      }

      applySavedMeta(saved);

      if (stayOnPage) {
        markCleanStayOnPage(sectionsToSend);
        setDraftToast('Saved as Draft');
      } else {
        setFeedback({
          success: `Draft ${formatQuotationVersion(saved)} saved for ${
            header.client || 'client'
          } - ${formatCurrency(saved.finalAmount ?? finalAmount)}. Hidden from the client until you submit. Returning to Quotations...`,
          error: '',
        });
        markCleanAndLeaveSoon();
      }
    } catch (error) {
      setFeedback({ success: '', error: formatApiError(error) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraftRef = useRef(handleSaveDraft);
  handleSaveDraftRef.current = handleSaveDraft;

  // Ctrl/Cmd+S → Save as Draft in place (no navigation; toast confirms)
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      if (event.key !== 's' && event.key !== 'S') return;
      event.preventDefault();
      handleSaveDraftRef.current?.({ stayOnPage: true });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!draftToast) return undefined;
    const timer = window.setTimeout(() => setDraftToast(''), 3000);
    return () => window.clearTimeout(timer);
  }, [draftToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowValidation(true);

    if (!siteId) {
      setFeedback({ success: '', error: 'Select a site before sending this quotation.' });
      return;
    }

    if (validationErrors.length > 0) {
      setFeedback({ success: '', error: 'Please fix the highlighted issues before saving.' });
      return;
    }

    // Read supersede id from navigation state at submit time (authoritative).
    const supersedeId = location.state?.supersedeQuoteId || supersedeQuoteId;
    const doingSupersede =
      Boolean(supersedeId) || location.state?.source === 'supersede' || isSuperseding;

    if (doingSupersede && !supersedeId) {
      setFeedback({
        success: '',
        error: 'Missing quotation to supersede. Go back to the list and click Supersede again.',
      });
      return;
    }

    setIsSaving(true);
    setFeedback({ success: '', error: '' });

    try {
      const sectionsToSend = await materializeSectionsForApi();
      const payload = { ...buildSnapshot('uploaded'), sections: sectionsToSend };
      // Never send old quoteId on supersede — new lineage only.
      if (doingSupersede) {
        delete payload.quoteId;
        delete payload.id;
      }
      let saved;

      if (doingSupersede) {
        saved = await interiorQuotationApi.supersedeQuotation(siteId, supersedeId, payload);
      } else if (apiQuoteId && isDraftQuote) {
        // Persist latest snapshot, then publish draft → uploaded.
        await interiorQuotationApi.updateQuotation(siteId, apiQuoteId, payload);
        saved = await interiorQuotationApi.submitQuotation(siteId, apiQuoteId);
      } else if (apiQuoteId) {
        saved = await interiorQuotationApi.updateQuotation(siteId, apiQuoteId, payload);
      } else if (preferDraftCreate) {
        const draft = await interiorQuotationApi.createQuotation(siteId, payload, { draft: true });
        saved = await interiorQuotationApi.submitQuotation(siteId, draft.quoteId);
      } else {
        saved = await interiorQuotationApi.createQuotation(siteId, payload);
      }

      applySavedMeta(saved);

      if (doingSupersede) {
        setFeedback({
          success: `New quotation ${saved.invoiceNumber || formatQuotationVersion(saved)} created (v${
            saved.version || 'N.0'
          }). Previous quote is now superseded. Returning to Quotations...`,
          error: '',
        });
      } else {
        const successVerb = isDraftQuote || preferDraftCreate
          ? 'submitted'
          : apiQuoteId
            ? 'updated'
            : 'sent';

        setFeedback({
          success: `Quotation ${formatQuotationVersion(saved)} ${successVerb} for ${
            header.client || 'client'
          } - Final amount ${formatCurrency(saved.finalAmount ?? finalAmount)}. Returning to Quotations...`,
          error: '',
        });
      }
      markCleanAndLeaveSoon();
    } catch (error) {
      setFeedback({ success: '', error: formatApiError(error) });
    } finally {
      setIsSaving(false);
    }
  };

  const primaryActionLabel = (() => {
    if (isSuperseding) return 'Supersede & Send';
    if (isDraftQuote) return 'Submit to Client';
    if (isClientFacingEdit) return 'Save Revision';
    return 'Send to Client';
  })();

  const versionPreviewLabel = (() => {
    if (isSuperseding) {
      return `Superseding ${
        supersedeFromInvoice || 'previous quote'
      } — Send creates a NEW quote_id (vN.0); old quote becomes superseded`;
    }
    if (isDraftQuote && metaVersion) {
      return `Editing draft ${formatQuotationVersion(metaVersion)} - save keeps it private; submit publishes to the client`;
    }
    if (apiQuoteId && metaVersion) {
      const major = metaVersion.majorVersion ?? 1;
      const minor = metaVersion.minorVersion ?? 0;
      return `Editing ${formatQuotationVersion(metaVersion)} - save will create v${major}.${minor + 1} (same quote_id)`;
    }
    if (preferDraftCreate) {
      return 'New draft - Save as Draft keeps it private on Stage; Submit publishes to the client';
    }
    return 'New quotation - Send to Client creates vN.0 on this site';
  })();

  return (
    <div className="w-full min-h-screen pb-16">
      {draftToast && typeof document !== 'undefined'
        ? createPortal(
            <div
              role="status"
              className="pointer-events-none fixed z-[9999] flex items-center gap-2 rounded-xl border border-green-200 bg-white px-4 py-3 text-sm font-semibold text-green-700 shadow-lg"
              style={{
                top: 'max(1rem, env(safe-area-inset-top))',
                right: 'max(1rem, env(safe-area-inset-right))',
              }}
            >
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              {draftToast}
            </div>,
            document.body
          )
        : null}
      <div className="mx-auto max-w-[1920px]">
        <button
          type="button"
          onClick={() => requestLeave('/onboarding-interior-quotation')}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-teal-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Quotations
        </button>
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            {isSuperseding
              ? 'Supersede Interior Quotation'
              : isDraftQuote
                ? 'Edit Draft Quotation'
                : isClientFacingEdit
                  ? 'Revise Interior Quotation'
                  : 'Create Interior Quotation'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Snapshots save as JSON on Stage. Line-item images upload via Interior files before save.
            Drafts stay private until you submit to the client.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700">
            {versionPreviewLabel}
          </span>
        </header>

        {isLoadingRecord ? (
          <div className="flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-24 text-sm text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
            Loading quotation…
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1 space-y-6">
            {/* Header fields */}
            <section className={cardClass}>
              <p className="text-sm font-semibold text-gray-900">Quotation Details</p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelClass}>Date Created</label>
                  <input
                    type="date"
                    value={header.dateCreated}
                    max={getTodayDate()}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next && next > getTodayDate()) return;
                      handleHeaderChange('dateCreated', next);
                    }}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Document Owner</label>
                  <input
                    type="text"
                    value={header.documentOwner}
                    onChange={(e) => handleHeaderChange('documentOwner', e.target.value)}
                    placeholder="e.g. Mahesh Babu"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Client</label>
                  <input
                    type="text"
                    value={header.client}
                    onChange={(e) => handleHeaderChange('client', e.target.value)}
                    placeholder="Client name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Project</label>
                  <input
                    type="text"
                    value={header.project}
                    onChange={(e) => handleHeaderChange('project', e.target.value)}
                    placeholder="Project / site name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Company Name</label>
                  <input
                    type="text"
                    value={header.companyName}
                    onChange={(e) => handleHeaderChange('companyName', e.target.value)}
                    placeholder="Company name"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            {/* Dynamic sections */}
            {sections.map((section, sectionIdx) => {
              const roomActions = roomActionsFor(section);
              const sectionTotal = section.isPriced ? sumSectionTotal(section.rooms) : null;

              return (
                <SectionAccordion
                  key={section.id}
                  section={section}
                  totalLabel={
                    section.isPriced ? formatCurrency(sectionTotal) : 'Not included in totals'
                  }
                  onToggle={() => updateSection(section.id, { collapsed: !section.collapsed })}
                  onRename={(name) => updateSection(section.id, { name })}
                  onTogglePriced={() => updateSection(section.id, { isPriced: !section.isPriced })}
                  onMoveUp={() => moveSection(section.id, -1)}
                  onMoveDown={() => moveSection(section.id, 1)}
                  canMoveUp={sectionIdx > 0}
                  canMoveDown={sectionIdx < sections.length - 1}
                  onRemove={() => removeSection(section.id)}
                  canRemove={sections.length > 1}
                >
                  {!section.isPriced && (
                    <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>
                        Informational section - quantities/notes only, paid directly to a vendor by the client.
                        Never contributes to Total (Priced Sections) or any figure below.
                      </span>
                    </div>
                  )}
                  {section.rooms.map((room, idx) => (
                    <RoomAccordion
                      key={room.id}
                      room={room}
                      subtotalLabel={section.isPriced ? formatCurrency(sumRoomTotal(room.items)) : null}
                      onToggle={() => roomActions.toggleCollapsed(room.id)}
                      onRename={(name) => roomActions.rename(room.id, name)}
                      onMoveUp={() => roomActions.moveRoom(room.id, -1)}
                      onMoveDown={() => roomActions.moveRoom(room.id, 1)}
                      canMoveUp={idx > 0}
                      canMoveDown={idx < section.rooms.length - 1}
                      onRemove={() => roomActions.removeRoom(room.id)}
                      canRemove={section.rooms.length > 1}
                    >
                      {section.isPriced ? (
                        <RoomItemsTable
                          items={room.items}
                          siteId={siteId}
                          onItemsChange={(items) => roomActions.updateItems(room.id, items)}
                        />
                      ) : (
                        <SectionCItemsTable
                          items={room.items}
                          siteId={siteId}
                          onItemsChange={(items) => roomActions.updateItems(room.id, items)}
                        />
                      )}
                    </RoomAccordion>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      roomActions.addRoom(() =>
                        section.isPriced ? createRoom('New Room') : createSectionCRoom('New Category')
                      )
                    }
                    className={addRoomBtnClass}
                  >
                    <Plus className="h-4 w-4" /> {section.isPriced ? 'Add Room' : 'Add Category'}
                  </button>
                </SectionAccordion>
              );
            })}

            <button type="button" onClick={addSection} className={addSectionBtnClass}>
              <Plus className="h-4 w-4" /> Add Section
            </button>

            {/* Static template text blocks */}
            <section className={cardClass}>
              <p className="text-sm font-semibold text-gray-900">General Terms and Condition</p>
              <RichTextEditor
                value={templates.generalTerms}
                onChange={(value) => setTemplates((prev) => ({ ...prev, generalTerms: value }))}
                placeholder="General terms and conditions..."
                minHeight={220}
                className="mt-3 bg-gray-50"
              />
            </section>

            <section className={cardClass}>
              <p className="text-sm font-semibold text-gray-900">Material Specification - Premium Segment</p>
              <RichTextEditor
                value={templates.materialSpec}
                onChange={(value) => setTemplates((prev) => ({ ...prev, materialSpec: value }))}
                placeholder="Material specification..."
                minHeight={300}
                className="mt-3 bg-gray-50"
              />
            </section>

            <section className={cardClass}>
              <p className="text-sm font-semibold text-gray-900">Design Terms and Condition</p>
              <RichTextEditor
                value={templates.designTerms}
                onChange={(value) => setTemplates((prev) => ({ ...prev, designTerms: value }))}
                placeholder="Design terms and conditions..."
                minHeight={180}
                className="mt-3 bg-gray-50"
              />
            </section>

            <section className={cardClass}>
              <p className="text-sm font-semibold text-gray-900">Important Notes</p>
              <RichTextEditor
                value={templates.importantNotes}
                onChange={(value) => setTemplates((prev) => ({ ...prev, importantNotes: value }))}
                placeholder="Important notes..."
                minHeight={180}
                className="mt-3 bg-gray-50"
              />
            </section>
          </div>

          {/* Totals sidebar */}
          <aside className="w-full flex-shrink-0 space-y-4 lg:sticky lg:top-6 lg:w-[380px]">
            <section className={cardClass}>
              <p className="text-sm font-semibold text-gray-900">Totals & Calculations</p>
              <dl className="mt-4 space-y-2.5 text-sm">
                {pricedSectionTotals.map((s) => (
                  <div key={s.id} className="space-y-1.5 rounded-lg border border-gray-100 bg-gray-50/50 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <dt className="truncate font-medium text-gray-700" title={s.name}>
                        {s.name || 'Unnamed section'}
                      </dt>
                      <dd className="flex-shrink-0 rounded-md bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                        {formatCurrency(s.total)}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-2 pl-1">
                      <dt className="flex items-center gap-1.5 text-xs text-gray-500">
                        Discount
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={s.discountPercent}
                          onFocus={() => {
                            if (String(s.discountPercent) === '0') {
                              updateSection(s.id, { discountPercent: '' });
                            }
                          }}
                          onBlur={() => {
                            if (s.discountPercent === '' || s.discountPercent == null) {
                              updateSection(s.id, { discountPercent: '0' });
                            }
                          }}
                          onChange={(e) =>
                            updateSection(s.id, {
                              discountPercent: sanitizePercentInput(e.target.value),
                            })
                          }
                          className="h-7 w-14 rounded-md border border-gray-200 bg-white px-1.5 text-right text-xs focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                        />
                        <span>%</span>
                      </dt>
                      <dd className="text-xs font-medium text-gray-600">{formatCurrency(s.afterDiscount)}</dd>
                    </div>
                  </div>
                ))}
                {pricedSectionTotals.length === 0 && (
                  <p className="text-xs text-gray-400">
                    No priced sections yet - mark a section "Priced" to include it here.
                  </p>
                )}
                <div className="flex items-center justify-between border-t border-gray-100 pt-2.5">
                  <dt className="font-medium text-gray-700">Total (Priced Sections)</dt>
                  <dd className="rounded-md bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                    {formatCurrency(totalPriced)}
                  </dd>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 pt-2.5">
                  <dt className="font-medium text-gray-700">Total After Discount</dt>
                  <dd className="rounded-md bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                    {formatCurrency(totalAfterDiscount)}
                  </dd>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <dt className="text-gray-500">Design & Coordination Fee</dt>
                  <dd className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={designFeePercent}
                      onChange={(e) => setDesignFeePercent(e.target.value)}
                      className={percentInputClass}
                    />
                    <span className="text-gray-400">%</span>
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Fee Amount</dt>
                  <dd className="rounded-md bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                    {formatCurrency(designFeeAmount)}
                  </dd>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 pt-2.5">
                  <dt className="font-medium text-gray-700">Amount with Design Fee</dt>
                  <dd className="rounded-md bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                    {formatCurrency(amountWithDesignFee)}
                  </dd>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <dt className="text-gray-500">GST</dt>
                  <dd className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={gstPercent}
                      onChange={(e) => setGstPercent(e.target.value)}
                      className={percentInputClass}
                    />
                    <span className="text-gray-400">%</span>
                  </dd>
                </div>
                <p className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-400">
                  <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
                  Real quotes often bill a blended/effective GST rate lower than the flat 18% label - adjust as
                  needed for this client.
                </p>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">GST Amount</dt>
                  <dd className="rounded-md bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                    {formatCurrency(gstAmount)}
                  </dd>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 pt-2.5 text-base">
                  <dt className="font-bold text-gray-900">Final Amount Incl. Taxes</dt>
                  <dd className="rounded-md bg-teal-50 px-2.5 py-1 font-bold text-teal-700">
                    {formatCurrency(finalAmount)}
                  </dd>
                </div>
              </dl>
            </section>

            <section className={cardClass}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Payment Schedule</p>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    paymentSumWarning ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'
                  }`}
                >
                  {paymentPercentSum}%
                </span>
              </div>
              <div className="mt-3 space-y-3">
                {paymentRows.map((row) => (
                  <div key={row.id} className="rounded-xl border border-gray-100 bg-gray-50/60 p-2.5">
                    <input
                      type="text"
                      value={row.label}
                      onChange={(e) => handlePaymentRowChange(row.id, 'label', e.target.value)}
                      className="mb-1.5 w-full rounded-md border border-transparent bg-transparent px-1 text-xs font-medium text-gray-700 hover:border-gray-200 focus:border-teal-400 focus:bg-white focus:outline-none"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={row.percent}
                          onChange={(e) => handlePaymentRowChange(row.id, 'percent', e.target.value)}
                          className={percentInputClass}
                        />
                        <span className="text-gray-400">%</span>
                      </div>
                      <span className="rounded-md bg-white px-2 py-0.5 text-sm font-semibold text-gray-800 shadow-sm">
                        {formatCurrency(row.amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {paymentSumWarning && (
                <p className="mt-2.5 flex items-start gap-1.5 text-xs text-amber-700">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  Percentages sum to {paymentPercentSum}%, not 100%. Adjust before sharing with the client.
                </p>
              )}
            </section>

            {showValidation && validationErrors.length > 0 && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <p className="flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-4 w-4" /> Fix before saving
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                  {validationErrors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            {feedback.error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {feedback.error}
              </div>
            )}
            {feedback.success && (
              <div className="flex items-start gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{feedback.success}</span>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
            >
              <Eye className="h-4 w-4" /> Preview
            </button>

            <div className="flex gap-3">
              {canSaveAsDraft && (
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={isSaving || !siteId}
                  className="h-12 flex-1 rounded-xl border border-teal-600 bg-white text-sm font-semibold text-teal-700 shadow-sm transition-colors hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSaving ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                    </span>
                  ) : isDraftQuote ? (
                    'Save Draft'
                  ) : (
                    'Save as Draft'
                  )}
                </button>
              )}
              <button
                type="submit"
                disabled={isSaving || !siteId}
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-teal-600 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                {primaryActionLabel}
              </button>
            </div>
          </aside>
        </form>
        )}
      </div>

      <PreviewInteriorQuatation
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        siteId={siteId}
        header={header}
        sections={sections}
        pricedSectionTotals={pricedSectionTotals}
        totalPriced={totalPriced}
        totalAfterDiscount={totalAfterDiscount}
        designFeePercent={designFeePercent}
        designFeeAmount={designFeeAmount}
        amountWithDesignFee={amountWithDesignFee}
        gstPercent={gstPercent}
        gstAmount={gstAmount}
        finalAmount={finalAmount}
        paymentRows={paymentRows}
        templates={templates}
        invoiceLabel={versionPreviewLabel}
        documentVersion={
          metaVersion?.version
            ? formatQuotationVersion(metaVersion)
            : ''
        }
      />

      <ConfirmDialog
        isOpen={leaveConfirmOpen}
        title="Leave without saving?"
        message="You have unsaved changes. If you leave now, the data you entered on this quotation will be lost."
        confirmLabel="Leave"
        cancelLabel="Stay"
        confirmTone="danger"
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
    </div>
  );
};

export default CreateInteriorQuatation;
